import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from '../../../core/services/toast.service';
import { LucideAngularModule, X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-angular';

/**
 * Toast Container Component
 * Displays all active toast notifications
 * 
 * Usage: Add to app.html:
 * ```
 * <app-toast-container />
 * ```
 */
@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="toast-container">
      @for (toast of toasts(); track toast.id) {
        <div 
          class="toast"
          [class.toast-success]="toast.type === 'success'"
          [class.toast-error]="toast.type === 'error'"
          [class.toast-warning]="toast.type === 'warning'"
          [class.toast-info]="toast.type === 'info'"
        >
          <div class="toast-icon">
            @switch (toast.type) {
              @case ('success') {
                <lucide-icon name="check-circle" [size]="20"></lucide-icon>
              }
              @case ('error') {
                <lucide-icon name="alert-circle" [size]="20"></lucide-icon>
              }
              @case ('warning') {
                <lucide-icon name="alert-triangle" [size]="20"></lucide-icon>
              }
              @case ('info') {
                <lucide-icon name="info" [size]="20"></lucide-icon>
              }
            }
          </div>
          
          <div class="toast-message">{{ toast.message }}</div>
          
          @if (toast.duration) {
            <button 
              class="toast-close"
              (click)="dismiss(toast.id)"
              aria-label="Dismiss"
            >
              <lucide-icon name="x" [size]="16"></lucide-icon>
            </button>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .toast-container {
      position: fixed;
      top: 1rem;
      right: 1rem;
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      max-width: 400px;
    }

    .toast {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 1rem;
      border-radius: 0.5rem;
      background: white;
      border: 1px solid;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      animation: slideIn 0.3s ease-out;
    }

    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    .toast-success {
      border-color: rgb(34, 197, 94);
      background: rgb(240, 253, 244);
    }

    .toast-error {
      border-color: rgb(239, 68, 68);
      background: rgb(254, 242, 242);
    }

    .toast-warning {
      border-color: rgb(234, 179, 8);
      background: rgb(254, 252, 232);
    }

    .toast-info {
      border-color: rgb(59, 130, 246);
      background: rgb(239, 246, 255);
    }

    .toast-icon {
      flex-shrink: 0;
    }

    .toast-success .toast-icon {
      color: rgb(34, 197, 94);
    }

    .toast-error .toast-icon {
      color: rgb(239, 68, 68);
    }

    .toast-warning .toast-icon {
      color: rgb(234, 179, 8);
    }

    .toast-info .toast-icon {
      color: rgb(59, 130, 246);
    }

    .toast-message {
      flex: 1;
      color: rgb(31, 41, 55);
      font-size: 0.875rem;
      font-weight: 500;
    }

    .toast-close {
      flex-shrink: 0;
      background: none;
      border: none;
      padding: 0.25rem;
      cursor: pointer;
      color: rgb(107, 114, 128);
      border-radius: 0.25rem;
      transition: all 0.2s;
    }

    .toast-close:hover {
      background: rgba(0, 0, 0, 0.05);
      color: rgb(31, 41, 55);
    }

    @media (max-width: 640px) {
      .toast-container {
        left: 1rem;
        right: 1rem;
        max-width: none;
      }
    }
  `],
})
export class ToastContainerComponent {
  private toastService = inject(ToastService);
  protected toasts = this.toastService.toasts;

  protected dismiss(id: string): void {
    this.toastService.remove(id);
  }
}


